import { getLogger } from '@logtape/logtape';
import { Card, Tooltip } from '@mantine/core';
import { useToggle } from '@mantine/hooks';
import { FC, MouseEvent, useMemo } from 'react';

import StatusDot from '@/components/StatusDot.tsx';
import { AppointmentPill, getCollectionPillColor } from '@/components/appointment-pill.tsx';

import useBreakpoints from '@/hooks/useBreakpoints.ts';
import { usePatientAppointmentForms } from '@/hooks/usePatientAppointmentForms.ts';

import { CalendarPosition } from '@/models/Calendar.ts';
import StatusDotType from '@/models/StatusDotType.ts';
import { DailyAppointment, Priority } from '@/models/appointment/Appointment.ts';

import { addPatientToAppointment } from '@/services/appointmentService.ts';
import { showWarningNotification } from '@/services/notificationService.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';
import { useCalendarStore } from '@/stores/calendarStore.ts';
import { WalkInModalMode, useWalkInStore } from '@/stores/walkInStore.ts';

import { getEternoPatientId, getInstanceId, getPvsPatientId } from '@/utils/appointmentUtils.ts';
import {
    HOVER_HEIGHT_EXPANSION_PERCENTAGE,
    MAX_APPOINTMENT_WIDTH,
    calculateAppointmentHeight,
    calculateAppointmentTop,
    calendarColor,
} from '@/utils/calendarUtils.ts';
import { isLightColor } from '@/utils/colorUtils.ts';
import { getInsuranceColor } from '@/utils/insurance-utils.ts';
import { openPatientModal, openWalkInModal } from '@/utils/modalUtils.tsx';
import { getInsuranceByKey, getPatientDisplayName } from '@/utils/patientUtils.ts';
import { cn } from '@/utils/styleUtils.ts';

import PriorityMarker from '../PriorityMarker.tsx';
import AppointmentOverview from './AppointmentOverview.tsx';

const logger = getLogger(['component', 'appointment-card']);

interface Props {
    appointment: DailyAppointment;
    timeslotHeight: number;
    containerHeight: number;
    color: string;
    preCalculatedPosition: CalendarPosition;
    mutateAppointments: () => void;
}

const AppointmentCard: FC<Props> = ({
    appointment,
    timeslotHeight,
    containerHeight,
    color,
    mutateAppointments,
    preCalculatedPosition,
}) => {
    const [is_expanded, toggle] = useToggle([false, true]);
    const is_tooltip_enabled = useCalendarStore((state) => state.is_tooltip_enabled);
    const is_color_bg_enabled = useCalendarStore((state) => state.is_color_bg_enabled);
    const { formatted_forms_for_appointment, forms_resolution, triggerAppointmentFormsData, is_loading_forms } =
        usePatientAppointmentForms(appointment, {
            lazy: true,
        });
    const is_valid_appointment = appointment?.patient?.account?.patient_number;
    const { is_less_than_xl } = useBreakpoints();

    const header_height = useCalendarStore((state) => state.header_height);
    const timeline_interval = useCalendarStore((state) => state.timeline_interval);

    const { top_position, height_percentage, left, width } = useMemo(() => {
        const base_top_position = calculateAppointmentTop(
            appointment.start,
            containerHeight,
            timeslotHeight,
            timeline_interval
        );

        const header_height_percentage = header_height ? (header_height / containerHeight) * 100 : 0;
        const top_position = base_top_position + header_height_percentage;

        const height_percentage = calculateAppointmentHeight(
            appointment.duration.value,
            containerHeight,
            timeslotHeight,
            timeline_interval
        );

        const { left, width } = preCalculatedPosition;
        return { top_position, height_percentage, left, width };
    }, [appointment, containerHeight, timeslotHeight, header_height, timeline_interval, preCalculatedPosition]);

    // Get insurance information
    const insurance = getInsuranceByKey(
        appointment?.data?.insurance_coverage_type?.toUpperCase() ?? appointment?.patient?.insurance?.type
    );

    // Handler for appointment click
    const handleAppointmentClick = (e: MouseEvent): void => {
        e.stopPropagation();
        if (useBookingStore.getState().isSeriesPlacementMode) {
            showWarningNotification('Termine können im Platzierungsmodus nicht bearbeitet werden');
            return;
        }

        if (!appointment.patient) {
            useWalkInStore.setState({
                selected_instance_id: getInstanceId(appointment),
                selected_appointment: appointment,
            });
            openWalkInModal(
                () => addPatientToAppointment(mutateAppointments),
                undefined,
                WalkInModalMode.ADD_PATIENT_TO_APPOINTMENT
            );
            return;
        }

        if (!is_valid_appointment) {
            return;
        }

        const eterno_patient_id = getEternoPatientId(appointment);
        const pvs_patient_id = getPvsPatientId(appointment);
        if (!pvs_patient_id) {
            logger.error('Missing patient data for appointment', { appointment: appointment });
            return;
        }
        const appt_instance_id: string | undefined = getInstanceId(appointment);
        if (!appt_instance_id) {
            logger.error('Missing instance id for appointment', { appointment: appointment });
            return;
        }

        openPatientModal(
            pvs_patient_id,
            eterno_patient_id,
            appt_instance_id,
            getPatientDisplayName(appointment) ?? '',
            appointment
        );
    };

    const show_animation = !is_tooltip_enabled && is_expanded && !is_less_than_xl;

    // Determine background and border colors based on appointment status
    const getBackgroundColor = (): string => {
        if (!is_valid_appointment) return '#f3f4f6';
        return is_color_bg_enabled ? color : '#f8fafc';
    };

    const getBorderLeftColor = (): string => {
        if (!is_valid_appointment) return '#d1d5db';
        return is_color_bg_enabled ? '' : color;
    };

    const calendar_color = calendarColor(appointment);
    const is_white_text_enabled = Boolean(is_color_bg_enabled && color && !isLightColor(color));

    const getTextColor = (): string => {
        if (!is_valid_appointment) return 'text-slate-500';
        if (is_white_text_enabled) return 'text-white';
        return 'text-slate-700';
    };

    return (
        <Tooltip
            label={
                <AppointmentOverview
                    appointment={appointment}
                    insurance={insurance}
                    formsResolution={forms_resolution}
                    formattedFormsForAppointment={formatted_forms_for_appointment}
                    isTooltip
                    isLoadingForms={is_loading_forms}
                />
            }
            position="top"
            multiline
            disabled={show_animation}
            transitionProps={{ transition: 'fade', duration: 200 }}
            classNames={{
                tooltip: 'border border-slate-200 bg-white text-slate-800 shadow-md',
                arrow: 'w-10 border-slate-200 bg-white',
            }}
        >
            <Card
                className={cn(
                    'absolute rounded border border-l-8 border-slate-200 bg-slate-100 p-0 transition-all duration-500',
                    is_valid_appointment ? 'cursor-pointer hover:border-slate-400' : 'cursor-default bg-striped-gray',
                    show_animation ? 'z-10 shadow-xl' : 'shadow-sm',
                    is_color_bg_enabled && 'border-l'
                )}
                //TODO: Clean style as soon as possible when we will figure out way for dynamic styles
                style={{
                    height: `${show_animation ? height_percentage + HOVER_HEIGHT_EXPANSION_PERCENTAGE : height_percentage}%`,
                    top: `${top_position}%`,
                    left: `${show_animation ? 0 : left}%`,
                    width: `${show_animation ? MAX_APPOINTMENT_WIDTH : width}%`,
                    backgroundColor: getBackgroundColor(),
                    borderLeftColor: getBorderLeftColor(),
                }}
                onClick={handleAppointmentClick}
                onMouseEnter={() => {
                    triggerAppointmentFormsData();
                    toggle();
                }}
                onMouseLeave={() => {
                    toggle();
                }}
            >
                <>
                    <div
                        className={cn(
                            'relative flex w-full items-center gap-1 transition-all',
                            show_animation ? 'h-auto p-1' : 'h-full p-0 2xl:h-6'
                        )}
                    >
                        {appointment?.collection_data && (
                            <AppointmentPill
                                text={`${appointment.collection_data.position}/${appointment.collection_data.total_appointments}`}
                                color={getCollectionPillColor(appointment)}
                                variant="filled"
                                size="small"
                            />
                        )}
                        <PriorityMarker priority={appointment.priority ?? Priority.NORMAL} />
                        <StatusDot size={10} color={calendar_color} type={StatusDotType.GENERIC} />
                        <div
                            className={cn(
                                'max-w-[70%] truncate transition-all',
                                getTextColor(),
                                show_animation ? 'text-sm' : 'text-xs'
                            )}
                        >
                            {getPatientDisplayName(appointment) ?? 'Patient'}
                        </div>
                        {insurance && (
                            <AppointmentPill
                                text={insurance}
                                color={getInsuranceColor(appointment.data.is_insurance_card_active, insurance)}
                            />
                        )}
                    </div>
                    <div
                        className={cn(
                            'flex h-[85%] flex-col justify-between gap-3 p-3 pt-1 transition duration-500',
                            show_animation ? 'opacity-100 delay-500' : 'opacity-0'
                        )}
                    >
                        <AppointmentOverview
                            appointment={appointment}
                            insurance={insurance}
                            formsResolution={forms_resolution}
                            formattedFormsForAppointment={formatted_forms_for_appointment}
                            isWhiteTextEnabled={is_white_text_enabled}
                            isLoadingForms={is_loading_forms}
                        />
                    </div>
                </>
            </Card>
        </Tooltip>
    );
};

export default AppointmentCard;
